// App-side delegate for `@heidi/templates/data` (D2.7 remainder, ARCFE-1055 #18785).
//
// The package hook owns the request and the three template-list invalidations.
// Everything below them stayed here, in its original order, because every
// statement of it was an app reaction: one analytics dispatch behind its
// `if (template)` runtime guard, the success toast, and — on the failure path —
// the two compliance dispatches with their modal atoms, plus the app-logger
// line and the destructive toast.
//
// The compliance CLASSIFICATION did move: deciding that a response body is a
// `ComplianceCheckFailedError` is data, so the package hands over the already-
// classified signal (`useMutateDuplicateTemplate`'s ARCFE-1046 shape) and this
// file only chooses which event and which modal.
import { regulatoryGuidelineBlockModalOpenAtom } from '@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-block-modal.atoms';
import { regulatoryGuidelineWarningModalOpenAtom } from '@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-warning-modal.atoms';
import { countMarkdownSections } from '@/helpers/sectionParser';
import {
  trackNhsTemplateComplianceBlock,
  trackNhsTemplateComplianceWarning,
} from '@/lib/analytics/scribe/compliance';
import { trackPDFTemplateCreated } from '@/lib/analytics/scribe/template';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useMutateCreatePDFTemplate as useMutateCreatePDFTemplateCore } from '@heidi/templates/data';
import { useSetAtom } from 'jotai';
import { useQueryConsultNoteTemplates } from '../query/useQueryConsultNoteTemplates';

// `export type { … }`, not `export { type … }` — the inline modifier reads as a
// VALUE export to tooling that inspects the declaration.
export type { CreatePDFTemplateProps } from '@heidi/templates/contracts';

export function useMutateCreatePDFTemplate() {
  // to trigger api call
  useQueryConsultNoteTemplates();

  const { t } = useTranslations();
  const setIsRegulatoryGuidelineBlockModalOpen = useSetAtom(
    regulatoryGuidelineBlockModalOpenAtom
  );
  const setIsRegulatoryGuidelineWarningModalOpen = useSetAtom(
    regulatoryGuidelineWarningModalOpenAtom
  );

  return useMutateCreatePDFTemplateCore({
    onCreated: (template) => {
      if (template) {
        trackPDFTemplateCreated({
          templateId: template.id,
          templateName: template.name,
          teamId: template.team_id,
          sectionCount: countMarkdownSections(template.structure_template),
        });
      }

      toast({
        tone: 'success',
        title: t('templateEditor.templateCreatedSuccess'),
      });
    },
    onComplianceRejected: ({ complianceOutputLength, passedComplianceCheck }) => {
      const isWarning = passedComplianceCheck;

      // PDF bodies live server-side, so there is no client-side content to size
      const complianceProps = {
        compliance_output_length: complianceOutputLength,
        template_source: 'pdf' as const,
      };
      if (isWarning) {
        trackNhsTemplateComplianceWarning(complianceProps);
        setIsRegulatoryGuidelineWarningModalOpen(true);
      } else {
        trackNhsTemplateComplianceBlock(complianceProps);
        setIsRegulatoryGuidelineBlockModalOpen(true);
      }
    },
    onRequestError: (error) => {
      logger.error('Error:', error);
      toast({
        tone: 'destructive',
        title: t('formFilling.failedToSaveTemplate'),
      });
    },
  });
}
